'use client';

import { useEffect, useRef } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { getPostHog, initAnalytics } from './lib/posthog-client';

/**
 * Headless manual `$pageview` tracker.
 *
 * PostHog is configured with `capture_pageview: false`, so we emit pageviews
 * ourselves on every App Router navigation (pathname or query-string change).
 * A ref holds the last captured URL so a re-render with the same URL — or the
 * initial mount racing PostHog's own bootstrap — never double-fires.
 *
 * Renders nothing. Uses `useSearchParams()`, so it must be wrapped in a
 * `<Suspense>` boundary by its parent.
 */
export function PageViewTracker(): null {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const lastUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Belt-and-braces: the provider inits in its render body, but re-run here so
    // a null client is never the reason a pageview is missed.
    initAnalytics();
    const ph = getPostHog();
    if (!ph) return;

    const url = window.location.href;
    if (url === lastUrlRef.current) return;
    lastUrlRef.current = url;

    ph.capture('$pageview', { $current_url: url });
  }, [pathname, searchParams]);

  return null;
}
